import type { Static } from "@sinclair/typebox";
import type { sessionSchema } from "src-server/utils/authenticated";

type User = {
  id: string;
  nickname: string;
  isPersonaVerified: boolean;
  score: number;
  scoreSymbolImageUrl: string;
  userLocations: {
    h3Index: number;
    name: string;
    current: boolean;
    checkinCount: number;
    rangeLevel: number;
    coordinates: {
      latitude: number;
      longitude: number;
    };
    countryCode: string;
  }[];
  userRegions: {
    id: number;
    name: string;
    current: boolean;
    checkinCount: number;
    regionRange: string;
    countryCode: string;
  }[];
  profileImageUrl: string;
  reviewCount: number;
};

export const getCurrentUserLocation = (
  userLocations: User["userLocations"],
  userRegions: User["userRegions"],
  session: Static<typeof sessionSchema>,
) => {
  /**
   * 세션에 저장된 위치가 있는지 확인
   */
  if (session.user?.webUserLocation) {
    const { webUserLocation } = session.user;

    const currentUserLocationFromSession = userLocations.find(
      (location) => location.h3Index === webUserLocation.h3Index,
    );

    /**
     * 세션에 저장된 위치가 있고, userLocations에 있을 경우 해당 지역 사용
     */
    if (currentUserLocationFromSession) {
      return {
        userLocation: currentUserLocationFromSession,
        userRegion: findRegionByLocationCurrent(
          currentUserLocationFromSession.current,
          userRegions,
        ),
      };
    }

    const currentUserLocation = userLocations.find(
      (location) => location.current,
    );

    if (!currentUserLocation) {
      throw new Error(
        "getCurrentUserLocation: the current location is not found",
      );
    }

    /**
     * userLocations에 없을 경우 userLocations의 current 지역 사용
     */
    return {
      userLocation: currentUserLocation,
      userRegion: findRegionByLocationCurrent(
        currentUserLocation.current,
        userRegions,
      ),
    };
  }

  /**
   * 세션에 저장된 위치가 없을 경우 userLocations의 current 지역 사용
   */
  const currentUserLocation = userLocations.find(
    (location) => location.current,
  );

  if (!currentUserLocation) {
    throw new Error(
      "getCurrentUserLocation: the current location is not found",
    );
  }

  const currentUserRegion = findRegionByLocationCurrent(
    currentUserLocation.current,
    userRegions,
  );

  return {
    userLocation: currentUserLocation,
    userRegion: currentUserRegion,
  };
};

/**
 * 현재 UserLocation <-> UserRegion 간 확인은 current field로만 가능
 */
export const findRegionByLocationCurrent = (
  current: boolean,
  userRegions: User["userRegions"],
) => {
  const currentUserRegion = userRegions.find(
    (region) => region.current === current,
  );

  if (!currentUserRegion) {
    throw new Error("findRegionByLocationCurrent: the region is not found");
  }

  return currentUserRegion;
};

/**
 * 현재 UserLocation <-> UserRegion 간 확인은 current field로만 가능
 */
export const findLocationByRegionCurrent = (
  current: boolean,
  userLocations: User["userLocations"],
) => {
  const currentUserLocation = userLocations.find(
    (location) => location.current === current,
  );

  if (!currentUserLocation) {
    throw new Error("findLocationByRegionCurrent: the location is not found");
  }

  return currentUserLocation;
};
